import { useState } from 'react';
import { api, type Goal } from '../api';
import { run } from '../hooks';
import type { EditorProps } from '../pages/SitePage';

const TYPES: { v: Goal['type']; label: string; hint: string }[] = [
  { v: 'form_submit', label: 'Form submitted', hint: 'Path pattern of the page with the form, e.g. /contact*' },
  { v: 'url_match', label: 'Page reached', hint: 'Path pattern, e.g. /thank-you*' },
  { v: 'cta_click', label: 'Link clicked', hint: 'URL pattern, e.g. tel:* or */book*' },
  { v: 'custom', label: 'Custom event', hint: "Call springboard('track','conversion',{goalId}) on your site" },
];

export function GoalsEditor({ bundle, reload, show }: EditorProps) {
  const { site, goals } = bundle;
  const [draft, setDraft] = useState<{ type: Goal['type']; label: string; pattern: string }>({ type: 'form_submit', label: '', pattern: '' });
  const patch = async (g: Goal, p: Partial<Goal>) => {
    const r = await run(api.updateGoal(site.siteId, g.goalId, p), show, 'Saved');
    if (r) await reload();
  };
  const add = async () => {
    const match = draft.type === 'cta_click' ? { urlPattern: draft.pattern } : draft.type === 'custom' ? {} : { pathPattern: draft.pattern };
    const r = await run(api.createGoal(site.siteId, { type: draft.type, label: draft.label || TYPES.find((t) => t.v === draft.type)!.label, match, isPrimary: goals.length === 0 }), show, 'Goal added');
    if (r) {
      setDraft({ type: 'form_submit', label: '', pattern: '' });
      await reload();
    }
  };
  return (
    <div>
      <p className="muted small" style={{ marginBottom: 16 }}>
        Goals are the conversions the A/B test measures for both groups (planning.md FR-W-15). Exactly one is primary. The widget never reads form field values.
      </p>
      <div className="card" style={{ padding: 0, marginBottom: 16 }}>
        <table>
          <thead>
            <tr>
              <th>Primary</th>
              <th>Type</th>
              <th>Label</th>
              <th>Match</th>
              <th>Source</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {goals.map((g) => (
              <tr key={g.goalId}>
                <td>
                  <input type="radio" name="primary" checked={g.isPrimary} onChange={() => patch(g, { isPrimary: true })} />
                </td>
                <td>{TYPES.find((t) => t.v === g.type)?.label ?? g.type}</td>
                <td>{g.label}</td>
                <td className="mono">{g.match.pathPattern ?? g.match.urlPattern ?? g.match.selector ?? (g.type === 'custom' ? g.goalId : '—')}</td>
                <td>
                  <span className={`badge ${g.source === 'manual' ? 'warn' : ''}`}>{g.source}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn sm danger"
                    onClick={async () => {
                      if (!confirm('Delete this goal?')) return;
                      const r = await run(api.deleteGoal(site.siteId, g.goalId), show, 'Goal deleted');
                      if (r !== null) await reload();
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {goals.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No goals yet. Add one so the A/B test has something to measure.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="card row">
        <select className="select" style={{ width: 180 }} value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as Goal['type'] })}>
          {TYPES.map((t) => (
            <option key={t.v} value={t.v}>
              {t.label}
            </option>
          ))}
        </select>
        <input className="input" style={{ width: 220 }} placeholder="Label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
        {draft.type !== 'custom' && (
          <input className="input mono" style={{ flex: 1, minWidth: 200 }} placeholder={TYPES.find((t) => t.v === draft.type)!.hint} value={draft.pattern} onChange={(e) => setDraft({ ...draft, pattern: e.target.value })} />
        )}
        {draft.type === 'custom' && <span className="small muted">{TYPES[3].hint}</span>}
        <button className="btn" onClick={add}>
          Add goal
        </button>
      </div>
    </div>
  );
}
